/**
 * The launch power meter shown during the countdown. A marker sweeps left↔right;
 * the player taps to lock it. The closer to the green sweet zone, the stronger
 * the launch (see game/launch.ts). Purely presentational + a sweeping marker.
 */
import Phaser from 'phaser';
import { LAUNCH } from '../game/launch.ts';
import { WORLD } from '../world.ts';

export class LaunchMeter {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly label: Phaser.GameObjects.Text;
  private readonly x = WORLD.width / 2 - 220;
  private readonly y = 250;
  private readonly w = 440;
  private readonly h = 26;

  private sweeping = false;
  private locked = false;
  private pos = 0; // 0..1 marker position
  private t = 0;

  constructor(scene: Phaser.Scene) {
    this.g = scene.add.graphics().setDepth(61).setVisible(false);
    this.label = scene.add
      .text(WORLD.width / 2, this.y - 24, 'TAP SPACE TO LAUNCH!', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#cfe0ff',
        stroke: '#000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(61)
      .setVisible(false);
  }

  begin(): void {
    this.sweeping = true;
    this.locked = false;
    this.pos = 0;
    this.t = 0;
    this.g.setVisible(true);
    this.label.setVisible(true);
    this.draw();
  }

  /** Advance the sweeping marker. No-op once locked/stopped. */
  update(dt: number): void {
    if (!this.sweeping || this.locked) return;
    this.t += dt * 2.2; // sweep speed
    this.pos = (Math.sin(this.t) + 1) / 2; // 0..1 ping-pong
    this.draw();
  }

  /** Lock the marker and return its 0..1 position. */
  lock(): number {
    this.locked = true;
    this.label.setText('LAUNCHED!').setColor('#ffd23b');
    this.draw();
    return this.pos;
  }

  /** Whether the marker is still sweeping (not yet locked). */
  get active(): boolean {
    return this.sweeping && !this.locked;
  }

  hide(): void {
    this.sweeping = false;
    this.g.setVisible(false);
    this.label.setVisible(false);
  }

  private draw(): void {
    const g = this.g;
    g.clear();
    // Track.
    g.fillStyle(0x000000, 0.5);
    g.fillRoundedRect(this.x - 3, this.y - 3, this.w + 6, this.h + 6, 5);
    g.fillStyle(0x222a38, 1);
    g.fillRect(this.x, this.y, this.w, this.h);
    // Sweet zone (green).
    const swX = this.x + (LAUNCH.sweetCenter - LAUNCH.sweetHalf) * this.w;
    const swW = LAUNCH.sweetHalf * 2 * this.w;
    g.fillStyle(0x2fd27a, 0.85);
    g.fillRect(swX, this.y, swW, this.h);
    // Marker.
    const mx = this.x + this.pos * this.w;
    g.fillStyle(this.locked ? 0xffd23b : 0xffffff, 1);
    g.fillRect(mx - 3, this.y - 6, 6, this.h + 12);
    // Border.
    g.lineStyle(2, 0xffffff, 0.3);
    g.strokeRect(this.x, this.y, this.w, this.h);
  }
}
